import { useState, type ReactNode } from 'react'
import { Box, Card, CardContent, Grid, Tab, Tabs } from '@mui/material'

import { useGetBreakdownQuery } from '@/apis/v2Api'
import type { BreakdownRow } from '@/apis/v2Mappers'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'
import { BreakdownTable, ReportHeader, ReportStatus } from '@/modules/reports/common'

export interface DimensionTab {
    dim: string
    label: string
    /** Count only this event (e.g. page_view for pages). */
    event?: string
    countLabel?: string
    /** Optional chart rendered next to the table. */
    chart?: (rows: BreakdownRow[]) => ReactNode
}

interface DimensionReportProps {
    title: string
    subtitle?: string
    tabs: DimensionTab[]
    emptyIcon: ReactNode
    emptyTitle: string
    emptyDescription?: string
    defaultPeriod?: Period
}

/** One v2 `/reports/breakdown` per tab (dim + optional event). */
export function DimensionReport({
    title,
    subtitle,
    tabs,
    emptyIcon,
    emptyTitle,
    emptyDescription,
    defaultPeriod = '30d',
}: DimensionReportProps) {
    const [period, setPeriod] = useState<Period>(defaultPeriod)
    const [tab, setTab] = useState(0)
    const args = useReportArgs(period)
    const current = tabs[tab]
    const {
        data = [],
        isLoading,
        isFetching,
        error,
    } = useGetBreakdownQuery({
        ...args,
        dim: current.dim,
        event: current.event,
        limit: 100,
    })

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title={title}
                subtitle={subtitle}
                period={period}
                onPeriodChange={setPeriod}
            />
            {tabs.length > 1 && (
                <Tabs
                    value={tab}
                    onChange={(_, v: number) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    variant="scrollable"
                >
                    {tabs.map(t => (
                        <Tab key={t.dim} label={t.label} />
                    ))}
                </Tabs>
            )}
            <ReportStatus
                isLoading={isLoading || (isFetching && data.length === 0)}
                error={error}
                isEmpty={data.length === 0}
                emptyIcon={emptyIcon}
                emptyTitle={emptyTitle}
                emptyDescription={emptyDescription}
            >
                <Grid container spacing={2}>
                    {current.chart && <Grid size={{ xs: 12, md: 5 }}>{current.chart(data)}</Grid>}
                    <Grid size={{ xs: 12, md: current.chart ? 7 : 12 }}>
                        <Card>
                            <CardContent>
                                <BreakdownTable
                                    rows={data}
                                    keyLabel={current.label}
                                    countLabel={current.countLabel ?? 'Events'}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </ReportStatus>
        </Box>
    )
}
